import * as THREE from "three";
import React, { useMemo, useState } from 'react';
import { useThree } from '@react-three/fiber';
import { useSelector } from "react-redux";

import { const_var, params } from '../../../../../../redux/reducers/reducer';
import { controls } from "../../../../../../redux/reducers/common/eventReducer";

import { CSG } from "../../../../../../assets/libs/csg-lib";
import { extrudeSettings, roofPattern, roofPatternCurve, textureAnisotropy } from '../../../../../../utils/Functions';
import useMouseEvents from "../../../../../../hooks/useMouseEvents";
import { MaterialInnerRoof, MaterialOuterRoof, MaterialRoofRidge } from "../../../../CustomMaterial";

const RoofModel = () => {
    const rightWallCloseState = useSelector((state) => state.reducer.rightWallCloseState)

    const { gl } = useThree();

    const [overHangWidth, setOverHangWidth] = useState(0);
    const [overHangLength, setOverHangLength] = useState(0);
    const [isEqualHeight, setIsEqualHeight] = useState(false);
    const [isEqualFrontLeanHeight, setIsEqualFrontLeanHeight] = useState(false);
    const [isEqualBackLeanHeight, setIsEqualBackLeanHeight] = useState(false);
    const [overHangHorizon, setOverHangHorizon] = useState(0);
    const [patternLength, setPatternLength] = useState(0);
    const [patternDepth, setPatternDepth] = useState(params.leanR_p_d);
    const [patternPosition, setPatternPosition] = useState({x:0,y:0,z:0});
    const [isRoofPanel, setIsRoofPanel] = useState(false);
    const [curveRoofPositionZ, setCurveRoofPositionZ] = useState(0)
    const [curveRoofPatternDepth, setCurveRoofPatternDepth] = useState(0)

    const roofThickness = 0.005;
    const cbFrontBackWallOffset = 0.16;
    const wallThickness = 0.005
    const trimThickness = 0.2;
    const ridgeThickness = 0.5
    const offset = 0.16

    const texture_metalSprite = const_var.metalTexture;
    const verticalMap = (texture_metalSprite) ? texture_metalSprite.clone() : texture_metalSprite;
    const horizontalMap = (texture_metalSprite) ? texture_metalSprite.clone() : texture_metalSprite;
    if(horizontalMap && verticalMap){
        textureAnisotropy(gl, verticalMap, 1.3, 1.3, Math.PI);
        verticalMap.needsUpdate = true;
        horizontalMap.offset.x = 0.45
        textureAnisotropy(gl, horizontalMap, 1.3, 1.3, -Math.PI / 2);
        horizontalMap.offset.setY(params.o_v_h_n_g_e ? 0.5 : 0.15)
        horizontalMap.needsUpdate = true;
    }
    const roofPitchHeight = (params.b_l_t_r_pR / 12) * params.leanR_p_w;
    const trimOffsetHeight = (params.p_r_p / 12) * trimThickness;
    const roofAngle = Math.atan((roofPitchHeight - (isEqualHeight && trimOffsetHeight)) / (params.leanR_p_w - (isEqualHeight && trimThickness)))
    const roofCutShapeWidth = (Math.sqrt(Math.pow(params.leanR_p_w, 2) * 2))
    const leanRRoofWidth = Math.sqrt(Math.pow((params.leanR_p_w - ((isEqualHeight && params.p_r_s !== 1) ? trimThickness : 0)), 2) + Math.pow((roofPitchHeight - ((isEqualHeight && params.p_r_s !== 1) ? trimOffsetHeight : 0)), 2))
    const leanRRoofHeight = (params.b_l_t_r_pR / 12) * params.leanR_p_w;
    const leanRoofLeftHeight = (params.b_l_t_r_pF / 12) * params.leanF_p_w;
    const leanRoofRightHeight = (params.b_l_t_r_pB / 12) * params.leanB_p_w;
    const diagonalLength = leanRRoofWidth + overHangWidth;

    const canvasElement = document.getElementsByClassName("buildingcanvas")
    const isPattern = (canvasElement != undefined && canvasElement[0]?.clientWidth > 1050 ) ? true : false
    
    const roofRidgeOffsetHeight = 0.03 / Math.cos(roofAngle);

    const offSetValue = useMemo(() => {
        let offSetValue;
        if (params.p_r_s === 1) {
            const height =  Math.sin(roofAngle) * offset;
            offSetValue = (height == 0 || Math.tan(roofAngle) == 0) ? 0 : height / Math.tan(roofAngle)
        } else offSetValue = 0
        
        return offSetValue
    }, [roofAngle , params.p_r_s])


    const roofTexture = useMemo(() => {
        if (params.p_r_s === 1 || params.p_r_s === 2) return verticalMap;
        else if (params.p_r_s === 3) return horizontalMap;

    }, [horizontalMap, params.p_r_s, verticalMap]);

    useMemo(() => {
        if(params.p_h === leanRRoofHeight + params.leanR_p_h) {
            setIsEqualHeight(true)
        } else {
            setIsEqualHeight(false)
        }
        if(params.add_front_lean && (leanRRoofHeight + params.leanR_p_h === leanRoofLeftHeight + params.leanF_p_h) && ((params.p_w === params.leanF_p_d) || Number(params.leantoAlignmentFront) === 3))
         setIsEqualFrontLeanHeight(true);
        else setIsEqualFrontLeanHeight(false)
        if(params.add_back_lean && (leanRRoofHeight + params.leanR_p_h === leanRoofRightHeight + params.leanB_p_h) && ((params.p_w === params.leanB_p_d) || Number(params.leantoAlignmentBack) === 2))
         setIsEqualBackLeanHeight(true);
        else setIsEqualBackLeanHeight(false)
    }, [params.p_h, params.leanR_p_h, params.b_l_t_r_pR, params.b_l_t_r_pF, params.b_l_t_r_pB, leanRRoofHeight, leanRoofLeftHeight, leanRoofRightHeight, params.add_front_lean, params.add_back_lean, params.leanB_p_d, params.leanF_p_d, params.p_w, params.leantoAlignmentFront, params.leantoAlignmentBack, params.leanF_p_h, params.leanB_p_h])
  
    const lengthLeftOffset = useMemo(() => (
        (isEqualFrontLeanHeight && !isEqualBackLeanHeight && !isEqualHeight && (params.p_d === params.leanR_p_d || Number(params.leantoAlignmentRight) === 2)) || 
        (isEqualFrontLeanHeight && isEqualBackLeanHeight && !isEqualHeight && (params.p_d !== params.leanR_p_d && Number(params.leantoAlignmentRight) === 2)) ||
        (isEqualFrontLeanHeight && isEqualBackLeanHeight && !isEqualHeight && (params.p_d === params.leanR_p_d))
    ) ? 0.23 : overHangLength, [isEqualFrontLeanHeight, isEqualBackLeanHeight, isEqualHeight, params.p_d, params.leanR_p_d, params.leantoAlignmentRight, overHangLength ]);
    
    const lengthRightOffset = useMemo(() => (
        (!isEqualFrontLeanHeight && isEqualBackLeanHeight && !isEqualHeight && (params.p_d === params.leanR_p_d || Number(params.leantoAlignmentRight) === 3)) ||
        (isEqualFrontLeanHeight && isEqualBackLeanHeight && !isEqualHeight && (params.p_d !== params.leanR_p_d && Number(params.leantoAlignmentRight) === 3)) ||
        (isEqualFrontLeanHeight && isEqualBackLeanHeight && !isEqualHeight && (params.p_d === params.leanR_p_d))
    ) ? 0.23 : overHangLength, [isEqualFrontLeanHeight, isEqualBackLeanHeight, isEqualHeight, params.p_d, params.leanR_p_d, params.leantoAlignmentRight, overHangLength]);
    
    const patternLengthOffset = useMemo(() => {
        let offset = 0;
        if((isEqualFrontLeanHeight && !isEqualBackLeanHeight && !isEqualHeight && (params.p_d === params.leanR_p_d || Number(params.leantoAlignmentRight) === 2)) ||
        (isEqualFrontLeanHeight && isEqualBackLeanHeight && !isEqualHeight && (params.p_d !== params.leanR_p_d && Number(params.leantoAlignmentRight) === 2)) ||
            (!isEqualFrontLeanHeight && isEqualBackLeanHeight && !isEqualHeight && (params.p_d === params.leanR_p_d || Number(params.leantoAlignmentRight) === 3)) ||
            (isEqualFrontLeanHeight && isEqualBackLeanHeight && !isEqualHeight && (params.p_d !== params.leanR_p_d && Number(params.leantoAlignmentRight) === 3))){
                offset =  -(overHangLength - 0.23);
            } else if(isEqualFrontLeanHeight && isEqualBackLeanHeight && !isEqualHeight && (params.p_d === params.leanR_p_d)){
                offset = -(overHangLength - 0.23) * 2;
        } else offset = 0;
        return offset;

    }, [isEqualFrontLeanHeight, isEqualBackLeanHeight, isEqualHeight, params.p_d, params.leanR_p_d, params.leantoAlignmentRight, overHangLength]);

    const patternPositionOffset = useMemo(() => {
        let offset = 0;
        if(params.p_r_s === 1 || params.p_r_s === 2){
            if((isEqualBackLeanHeight && !isEqualHeight && (params.p_d === params.leanR_p_d || Number(params.leantoAlignmentRight) === 3)) 
                || (isEqualFrontLeanHeight && isEqualBackLeanHeight && !isEqualHeight && (params.p_d !== params.leanR_p_d && Number(params.leantoAlignmentRight) === 3))){
                    offset = -(overHangLength - 0.23)
            } else {
                offset = 0;
            }
        } else if(params.p_r_s === 3){
            if((isEqualFrontLeanHeight && !isEqualHeight && (params.p_d === params.leanR_p_d || Number(params.leantoAlignmentRight) === 2)) 
                || (isEqualFrontLeanHeight && isEqualBackLeanHeight && !isEqualHeight && (params.p_d !== params.leanR_p_d && Number(params.leantoAlignmentRight) === 2))){
                    offset = (overHangLength - 0.23)
            } else {
                offset = 0;
            }
        }

        return offset;
    }, [isEqualFrontLeanHeight, isEqualBackLeanHeight, isEqualHeight, params.p_d, params.leanR_p_d, params.leantoAlignmentRight, overHangLength, params.p_r_s])

    const regularPatternPositionOffset = useMemo(() => {
        let offset = 0;
        if(isEqualFrontLeanHeight && !isEqualHeight && (Number(params.leantoAlignmentRight) === 2 || params.leanR_p_d === params.p_d)){
                offset = -(overHangLength - 0.23)
        } else {
            offset = 0;
        }
        return offset;
    }, [isEqualFrontLeanHeight, isEqualBackLeanHeight, isEqualHeight, params.p_d, params.leanR_p_d, params.leantoAlignmentRight, overHangLength, params.p_r_s])
    
    useMemo(() => {
        setOverHangHorizon(Math.cos(roofAngle) * overHangWidth)
    }, [roofAngle, overHangWidth])
    
    let sideOverHangSize = 0 
    let endOverHangSize = 0 
    if(const_var.isOverhang['side']!=undefined && const_var.isOverhang['side'].size){
        sideOverHangSize = Number(const_var.isOverhang['side'].size)
        if(sideOverHangSize<=1){
            sideOverHangSize = (0.25 + sideOverHangSize)
        }
    }
    if(const_var.isOverhang['end']!=undefined && const_var.isOverhang['end'].size){
        endOverHangSize = Number(const_var.isOverhang['end'].size)
    }

    const roofPanModel = useMemo(() => {
        let offset = 0;
        if (params.p_r_s === 1) offset = 0.12;
        else offset = 0;
        
        const model = new THREE.Shape();

        if( params.add_right_back_lean_porch !== true && params.add_right_front_lean_porch !== true ) {
                model.moveTo(-(params.leanR_p_d / 2 + lengthLeftOffset), 0 + overHangWidth - offset);
                model.lineTo((params.leanR_p_d / 2 + lengthRightOffset), 0 + overHangWidth - offset);
                model.lineTo((params.leanR_p_d / 2 + lengthRightOffset), -leanRRoofWidth);
                model.lineTo(-(params.leanR_p_d / 2 + lengthLeftOffset), -leanRRoofWidth);
                model.closePath();

        } else if( params.add_right_back_lean_porch === true && params.add_right_front_lean_porch !== true ) {
            model.moveTo((params.leanR_p_d / 2 + params.leanR_p_w + cbFrontBackWallOffset + overHangHorizon - offSetValue), 0 + overHangWidth - offset);
            model.lineTo(-(params.leanR_p_d / 2 + lengthLeftOffset), 0 + overHangWidth - offset);
            model.lineTo(-(params.leanR_p_d / 2 + lengthLeftOffset), -leanRRoofWidth);
            model.lineTo((params.leanR_p_d / 2 + cbFrontBackWallOffset + ((isEqualHeight && params.p_r_s !== 1) ? trimThickness : 0)), -leanRRoofWidth);
            model.closePath();

        } else if( params.add_right_back_lean_porch !== true && params.add_right_front_lean_porch === true ) {
            model.moveTo(-(params.leanR_p_d / 2 + cbFrontBackWallOffset + overHangHorizon + params.leanR_p_w - offSetValue) , 0 + overHangWidth - offset);
            model.lineTo((params.leanR_p_d / 2 + lengthRightOffset), 0 + overHangWidth - offset);
            model.lineTo((params.leanR_p_d / 2 + lengthRightOffset), -leanRRoofWidth);
            model.lineTo(-(params.leanR_p_d /  2 + cbFrontBackWallOffset + ((isEqualHeight && params.p_r_s !== 1) ? trimThickness : 0)), -leanRRoofWidth);
            model.closePath();

        } else {
            model.moveTo(-((params.leanR_p_d / 2 + cbFrontBackWallOffset + overHangHorizon) + params.leanR_p_w - offSetValue), 0 + overHangWidth - offset);
            model.lineTo((params.leanR_p_d / 2 + cbFrontBackWallOffset + overHangHorizon + params.leanR_p_w - offSetValue), 0 + overHangWidth - offset);
            model.lineTo((params.leanR_p_d / 2 + cbFrontBackWallOffset + ((isEqualHeight && params.p_r_s !== 1) ? trimThickness : 0)), -leanRRoofWidth);
            model.lineTo(-(params.leanR_p_d / 2 + cbFrontBackWallOffset + ((isEqualHeight && params.p_r_s !== 1) ? trimThickness : 0)), -leanRRoofWidth);
            model.closePath();
        }

        return model;
    }, [params.p_r_s, params.add_right_back_lean_porch, params.add_right_front_lean_porch, params.leanR_p_d, overHangWidth, overHangHorizon, params.leanR_p_w, params.b_l_t_r_pR, params.leanR_p_d, overHangWidth, overHangLength, leanRRoofWidth, params.leanR_p_h, isEqualHeight, params.o_v_h_n_g_e, params.o_v_h_n_g, params.p_r_s, lengthLeftOffset, lengthRightOffset, offSetValue]);

    const roofTopTrimRidgeModelObj = useMemo(() => {
        let rightTrimRidge;
        let leftTrimRidge;

        const roofTopLeftTrimRidgeModel = new THREE.Shape();
            roofTopLeftTrimRidgeModel.moveTo(-((params.leanR_p_d / 2) + params.leanR_p_w + overHangHorizon), 0 + overHangWidth);
            roofTopLeftTrimRidgeModel.lineTo(-((params.leanR_p_d / 2) + params.leanR_p_w + overHangHorizon) + ridgeThickness, 0 + overHangWidth);
            roofTopLeftTrimRidgeModel.lineTo(-(params.leanR_p_d / 2 + (isEqualHeight ? trimThickness : 0)) + ridgeThickness, -leanRRoofWidth);
            roofTopLeftTrimRidgeModel.lineTo(-(params.leanR_p_d / 2 + (isEqualHeight ? trimThickness : 0)), -leanRRoofWidth);
            roofTopLeftTrimRidgeModel.closePath()

        const roofTopRightTrimRidgeModel = new THREE.Shape();
            roofTopRightTrimRidgeModel.moveTo((params.leanR_p_d / 2) + params.leanR_p_w + overHangHorizon - ridgeThickness, 0 + overHangWidth);
            roofTopRightTrimRidgeModel.lineTo((params.leanR_p_d / 2) + params.leanR_p_w + overHangHorizon, 0 + overHangWidth);
            roofTopRightTrimRidgeModel.lineTo((params.leanR_p_d / 2 + (isEqualHeight ? trimThickness : 0)), -leanRRoofWidth);
            roofTopRightTrimRidgeModel.lineTo((params.leanR_p_d / 2 - ridgeThickness + (isEqualHeight ? trimThickness : 0)), -leanRRoofWidth);
            roofTopRightTrimRidgeModel.closePath()

        const roofHeight = params.p_r_p / 12 * (params.p_w / 2 + 0.75)
        
        let lengthOffset;
        let delModelPosOffsetX;

           
        if(params.o_v_h_n_g_e && params.o_v_h_n_g) {
            if(sideOverHangSize === 1.05 && endOverHangSize === 1){
                lengthOffset = 2;
                delModelPosOffsetX = 1.05;
            } else if(sideOverHangSize === 2 && endOverHangSize === 1){
                lengthOffset = 2;
                delModelPosOffsetX = 2;
            } else if(endOverHangSize === 2 && sideOverHangSize === 2){
                if(params.p_r_s === 2){
                    lengthOffset = 2 + endOverHangSize / 2;
                } else if(params.p_r_s === 3){
                    lengthOffset = 2 + endOverHangSize;
                }
                delModelPosOffsetX = 2;
            } else if(endOverHangSize === 2 && sideOverHangSize === 1.05){
                if(params.p_r_s === 2){
                    lengthOffset = 2 + endOverHangSize / 2;
                } else if(params.p_r_s === 3){
                    lengthOffset = 2 + endOverHangSize;
                }
                delModelPosOffsetX = 1.05;
            } else {
                lengthOffset = 2;
                delModelPosOffsetX = 1.25;
            }
        } else if(!params.o_v_h_n_g_e && params.o_v_h_n_g) {
            if(sideOverHangSize === 1.05){
                delModelPosOffsetX = 1.05;
            } else if(sideOverHangSize === 2){
                delModelPosOffsetX = 2;
            } else {
                delModelPosOffsetX = 1.25;
            }
            lengthOffset = 1.5;
        } else if(params.o_v_h_n_g_e && !params.o_v_h_n_g) {
            if(endOverHangSize === 2){
                if(params.p_r_s === 2){
                    lengthOffset = 2 + endOverHangSize / 2;
                } else if(params.p_r_s === 3){
                    lengthOffset = 2 + endOverHangSize;
                }
            } else {
                lengthOffset = 2;
            }
            delModelPosOffsetX = 0.75;
        } else {
            lengthOffset = 1.5
            delModelPosOffsetX = 0.75;
        }

        const roofDelModel = new THREE.Shape();
        roofDelModel.moveTo(params.p_w / 2 + delModelPosOffsetX, params.p_h);
        roofDelModel.lineTo(0, params.p_h + roofHeight);
        roofDelModel.lineTo(0, 0);
        roofDelModel.lineTo(params.p_w / 2 + delModelPosOffsetX, 0);
        roofDelModel.closePath();

        const meshRoofTopRightTrimRidgeModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofTopRightTrimRidgeModel, extrudeSettings(0.03)), new THREE.MeshStandardMaterial({color: 'blue', side : THREE.DoubleSide}));
        const meshRoofTopLeftTrimRidgeModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofTopLeftTrimRidgeModel, extrudeSettings(0.03)), new THREE.MeshStandardMaterial({color: 'blue', side : THREE.DoubleSide}));
        const roofDelModelObj = new THREE.Mesh(new THREE.ExtrudeGeometry(roofDelModel, extrudeSettings(params.p_d + lengthOffset)), new THREE.MeshStandardMaterial({color: 'blue', side : THREE.DoubleSide}));
         
        const meshRoofTopRightTrimRidgeModel_Temp = meshRoofTopRightTrimRidgeModel.clone();
        meshRoofTopRightTrimRidgeModel_Temp.position.set((params.p_w / 2 + params.leanR_p_w), params.leanR_p_h + roofRidgeOffsetHeight + 0.068, -0.16);
        meshRoofTopRightTrimRidgeModel_Temp.rotation.set(0, Math.PI, Math.PI / 2 + roofAngle)
        meshRoofTopRightTrimRidgeModel_Temp.updateMatrixWorld();

        const meshRoofTopRightTrimRidgeModel_Temp1 = meshRoofTopRightTrimRidgeModel_Temp.clone();
        meshRoofTopRightTrimRidgeModel_Temp1.rotateY(-Math.PI / 2);
        meshRoofTopRightTrimRidgeModel_Temp1.updateMatrixWorld();

        const meshRoofTopLeftTrimRidgeModel_Temp = meshRoofTopLeftTrimRidgeModel.clone();
        meshRoofTopLeftTrimRidgeModel_Temp.position.set((params.p_w / 2 + params.leanR_p_w), params.leanR_p_h + roofRidgeOffsetHeight + 0.068, 0.16);
        meshRoofTopLeftTrimRidgeModel_Temp.rotation.set(0, Math.PI, Math.PI / 2 + roofAngle)
        meshRoofTopLeftTrimRidgeModel_Temp.updateMatrixWorld();

        const meshRoofTopLeftTrimRidgeModel_Temp1 = meshRoofTopLeftTrimRidgeModel_Temp.clone();
        meshRoofTopLeftTrimRidgeModel_Temp1.rotateY(-Math.PI / 2);
        meshRoofTopLeftTrimRidgeModel_Temp1.updateMatrixWorld();

        const roofDelModelObj_Temp = roofDelModelObj.clone();
        roofDelModelObj_Temp.position.set(0, 0, -params.p_d / 2 - lengthOffset / 2 - params.leanR_p_pos);
        roofDelModelObj_Temp.updateMatrixWorld();

        const meshRoofTopRightTrimRidgeModel_CSG = CSG.fromMesh(meshRoofTopRightTrimRidgeModel_Temp1);
        const meshRoofTopLeftTrimRidgeModel_CSG = CSG.fromMesh(meshRoofTopLeftTrimRidgeModel_Temp1);

        const roofDelModelObj_CSG = CSG.fromMesh(roofDelModelObj_Temp)

        let meshRoofTopRightTrimRidgeModelCSG;
        let meshRoofTopLeftTrimRidgeModelCSG;
        if(isEqualHeight){
            meshRoofTopRightTrimRidgeModelCSG = meshRoofTopRightTrimRidgeModel_CSG.subtract(roofDelModelObj_CSG);
            meshRoofTopLeftTrimRidgeModelCSG = meshRoofTopLeftTrimRidgeModel_CSG.subtract(roofDelModelObj_CSG);
        } else{
            meshRoofTopRightTrimRidgeModelCSG = meshRoofTopRightTrimRidgeModel_CSG
            meshRoofTopLeftTrimRidgeModelCSG = meshRoofTopLeftTrimRidgeModel_CSG
        }
        
        const CSGRoofTopRightTrimRidgeModel = CSG.toMesh(meshRoofTopRightTrimRidgeModelCSG, new THREE.Matrix4());
        const CSGRoofTopLeftTrimRidgeModel = CSG.toMesh(meshRoofTopLeftTrimRidgeModelCSG, new THREE.Matrix4());

        rightTrimRidge = CSGRoofTopRightTrimRidgeModel.geometry;
        leftTrimRidge = CSGRoofTopLeftTrimRidgeModel.geometry;

        return {rightTrimRidge, leftTrimRidge}

    }, [params.leanR_p_d, params.leanR_p_w, params.leanR_p_h, overHangWidth, overHangHorizon, params.b_l_t_r_pR, params.p_r_p, params.p_w, params.p_h, params.p_d, isEqualHeight, leanRRoofWidth, roofRidgeOffsetHeight, params.o_v_h_n_g, params.o_v_h_n_g_e, roofAngle, endOverHangSize, sideOverHangSize, params.p_r_s, params.leanR_p_pos])

    const roofModelObj = useMemo(() => {
        let mainOuter;
        let mainInner;
        let curveOuter;
        let curveInner;

        const curveInnerModel = new THREE.Shape();          
            curveInnerModel.moveTo(-0.1, 0.04);
            curveInnerModel.lineTo(-0.1, 0.04 + const_var.roofRegularDst);
            curveInnerModel.quadraticCurveTo(-0.1, const_var.roofRegularDst * 1.8 + (const_var.roofRegularDst * params.p_r_p / 12) / 10, 
                                            -0.15 + const_var.roofRegularDst, const_var.roofRegularDst * (2 + params.b_l_t_r_pR / 12) - 0.02);
            curveInnerModel.lineTo(params.leanR_p_w - 0.15, (params.leanR_p_w - 0.1) * params.b_l_t_r_pR / 12 + const_var.roofRegularDst * 2);
            curveInnerModel.lineTo(params.leanR_p_w - 0.15, (params.leanR_p_w - 0.1) * params.b_l_t_r_pR / 12 + const_var.roofRegularDst * 2 + 0.01);
            curveInnerModel.lineTo(-0.15 + const_var.roofRegularDst, const_var.roofRegularDst * (2 + params.b_l_t_r_pR / 12) - 0.01);
            curveInnerModel.quadraticCurveTo(-0.1, const_var.roofRegularDst * 1.8 + (const_var.roofRegularDst * params.p_r_p / 12) / 10, 
                                            -0.11, 0.04 + const_var.roofRegularDst);
            curveInnerModel.moveTo(-0.11, 0.04);
            curveInnerModel.closePath();
        
        const roofCutShape = new THREE.Shape();
            roofCutShape.moveTo(-roofCutShapeWidth - 2.5, 0);
            roofCutShape.lineTo(-roofCutShapeWidth - 2.5, params.leanR_p_h + (params.b_l_t_r_pR / 12*((params.leanR_p_w))) + 2);
            roofCutShape.lineTo(roofCutShapeWidth + 2.5, params.leanR_p_h + (params.b_l_t_r_pR / 12*((params.leanR_p_w))) + 2);
            roofCutShape.lineTo(roofCutShapeWidth + 2.5, 0);
            roofCutShape.closePath()
    
        const curveRoofCutShape = new THREE.Shape();
            curveRoofCutShape.moveTo(-params.leanR_p_w * 2, 0)
            curveRoofCutShape.lineTo(-params.leanR_p_w * 2, params.leanR_p_h * 3);
            curveRoofCutShape.lineTo(params.leanR_p_w * 2, params.leanR_p_h * 3);
            curveRoofCutShape.lineTo(params.leanR_p_w * 2, 0)

        const roofHeight = params.p_r_p / 12 * (params.p_w / 2 + 0.75)

        let lengthOffset;
        let delModelPosOffsetX;

        if(params.o_v_h_n_g_e && params.o_v_h_n_g) {
            if(sideOverHangSize === 1.05 && endOverHangSize === 1){
                lengthOffset = 2;
                delModelPosOffsetX = 1.05;
            } else if(sideOverHangSize === 2 && endOverHangSize === 1){
                lengthOffset = 2;
                delModelPosOffsetX = 2;
            } else if(endOverHangSize === 2 && sideOverHangSize === 2){
                if(params.p_r_s === 2){
                    lengthOffset = 2 + endOverHangSize / 2;
                } else if(params.p_r_s === 3){
                    lengthOffset = 2 + endOverHangSize;
                }
                delModelPosOffsetX = 2;
            } else if(endOverHangSize === 2 && sideOverHangSize === 1.05){
                if(params.p_r_s === 2){
                    lengthOffset = 2 + endOverHangSize / 2;
                } else if(params.p_r_s === 3){
                    lengthOffset = 2 + endOverHangSize;
                }
                delModelPosOffsetX = 1.05;
            } else {
                lengthOffset = 2;
                delModelPosOffsetX = 1.25;
            }
        } else if(!params.o_v_h_n_g_e && params.o_v_h_n_g) {
            if(sideOverHangSize === 1.05){
                delModelPosOffsetX = 1.05;
            } else if(sideOverHangSize === 2){
                delModelPosOffsetX = 2;
            } else {
                delModelPosOffsetX = 1.25;
            }
            lengthOffset = 1.5;
        } else if(params.o_v_h_n_g_e && !params.o_v_h_n_g) {
            if(endOverHangSize === 2){
                if(params.p_r_s === 2){
                    lengthOffset = 2 + endOverHangSize / 2;
                } else if(params.p_r_s === 3){
                    lengthOffset = 2 + endOverHangSize;
                }
            } else {
                lengthOffset = 2;
            }
            delModelPosOffsetX = 0.75;
        } else {
            lengthOffset = 1.5
            delModelPosOffsetX = 0.75;
        }

        const roofDelModel = new THREE.Shape();
        roofDelModel.moveTo(params.p_w / 2 + delModelPosOffsetX, params.p_h);
        roofDelModel.lineTo(0, params.p_h + roofHeight);
        roofDelModel.lineTo(0, 0);
        roofDelModel.lineTo(params.p_w / 2 + delModelPosOffsetX, 0);
        roofDelModel.closePath();

        const outerRoofMesh = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(patternLength,isPattern,"roof"), extrudeSettings(patternDepth)));
        const curveOuterMesh = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPatternCurve(2, params.b_l_t_r_pR.toString(), isPattern), extrudeSettings(curveRoofPatternDepth)));
        const curveInnerMesh = new THREE.Mesh(new THREE.ExtrudeGeometry(curveInnerModel, extrudeSettings(curveRoofPatternDepth)));

        const mainRoofCutMesh = new THREE.Mesh(new THREE.ExtrudeGeometry(roofCutShape, extrudeSettings(roofCutShapeWidth)));
        const curveRoofCutMesh = new THREE.Mesh(new THREE.ExtrudeGeometry(curveRoofCutShape, extrudeSettings(params.leanR_p_w * 2)));

        const roofDelModelObj = new THREE.Mesh(new THREE.ExtrudeGeometry(roofDelModel, extrudeSettings(params.p_d + lengthOffset)), new THREE.MeshStandardMaterial({color: 'blue', side : THREE.DoubleSide}));

        let curveRoofCutMesh1 = curveRoofCutMesh.clone();
        curveRoofCutMesh1.rotation.set(0, -Math.PI / 4, 0);
        curveRoofCutMesh1.position.set(params.p_w / 2 + params.leanR_p_w + overHangWidth, 0, params.p_d / 2 + params.leanR_p_w + overHangWidth + cbFrontBackWallOffset - params.leanR_p_pos);
        curveRoofCutMesh1.updateMatrixWorld();

        let curveRoofCutMesh2 = curveRoofCutMesh.clone();
        curveRoofCutMesh2.rotation.set(0, Math.PI / 4 * 5, 0);
        curveRoofCutMesh2.position.set(params.p_w / 2 + params.leanR_p_w + overHangWidth, 0, -(params.p_d / 2 + params.leanR_p_w + overHangWidth + cbFrontBackWallOffset) - params.leanR_p_pos);
        curveRoofCutMesh2.updateMatrixWorld();

        let roofCutMesh1 = mainRoofCutMesh.clone();
        roofCutMesh1.rotation.set(0, -Math.PI / 4, 0);
        roofCutMesh1.position.set(params.p_w / 2 + params.leanR_p_w, 0, params.p_d / 2 + params.leanR_p_w + cbFrontBackWallOffset - params.leanR_p_pos);
        roofCutMesh1.visible = false;
        roofCutMesh1.updateMatrixWorld();
        
        let roofCutMesh2 = mainRoofCutMesh.clone();
        roofCutMesh2.rotation.set(0, Math.PI / 4 * 5, 0);
        roofCutMesh2.position.set(params.p_w / 2 + params.leanR_p_w, 0, -(params.p_d / 2 + params.leanR_p_w + cbFrontBackWallOffset) - params.leanR_p_pos);
        roofCutMesh2.visible = false;
        roofCutMesh2.updateMatrixWorld();

        let outerRoofMeshPosUpdate = outerRoofMesh.clone();
        outerRoofMeshPosUpdate.position.set(patternPosition.x,patternPosition.y,patternPosition.z);
        outerRoofMeshPosUpdate.rotation.set(Math.PI / 2,(params.p_r_s === 3) ? Math.PI : Math.PI / -2,Math.PI)
        outerRoofMeshPosUpdate.updateMatrixWorld();

        // Create a temporary object to apply the parent group's transformations
        let tempGroup1 = new THREE.Object3D();
        tempGroup1.rotation.set(0, -Math.PI / 2, 0);
        tempGroup1.updateMatrixWorld();
        
        let tempGroup2 = new THREE.Object3D();
        tempGroup2.position.set((params.p_w / 2 + params.leanR_p_w), params.leanR_p_h + 0.025 +  + (params.p_r_s === 1 ? const_var.roofRegularDst * 2 + 0.14 * (params.b_l_t_r_pR / 12) - 0.015: 0), 0)
        tempGroup2.rotation.set(0, Math.PI, (Math.PI / 2 + roofAngle));
        tempGroup2.updateMatrixWorld();

        // Apply the parent's world matrix to the outerRoofMeshPosUpdate
        outerRoofMeshPosUpdate.applyMatrix4(tempGroup1.matrixWorld);
        outerRoofMeshPosUpdate.updateMatrixWorld();

        outerRoofMeshPosUpdate.applyMatrix4(tempGroup2.matrixWorld)
        outerRoofMeshPosUpdate.updateMatrixWorld();

        // Now roofmesh has the combined transformations

        let curveOuterMeshPosUpdate = curveOuterMesh.clone();
        curveOuterMeshPosUpdate.position.set(params.p_w / 2 + params.leanR_p_w - 0.126 + 0.14, params.leanR_p_h + 0.1*(params.b_l_t_r_pR/12) + 0.035, curveRoofPositionZ);
        curveOuterMeshPosUpdate.rotation.set(0, Math.PI, 0);
        curveOuterMeshPosUpdate.visible = false
        curveOuterMeshPosUpdate.updateMatrixWorld();

        let innerOuterMeshPosUpdate = curveInnerMesh.clone();
        innerOuterMeshPosUpdate.position.set(params.p_w / 2 + params.leanR_p_w - 0.126, params.leanR_p_h + 0.1*(params.b_l_t_r_pR/12), curveRoofPositionZ);
        innerOuterMeshPosUpdate.rotation.set(0, Math.PI, 0);
        innerOuterMeshPosUpdate.visible = false
        innerOuterMeshPosUpdate.updateMatrixWorld();

        const roofDelModelObj_Temp = roofDelModelObj.clone();
        roofDelModelObj_Temp.position.set(0, 0, -params.p_d / 2 - lengthOffset / 2 - params.leanR_p_pos);
        roofDelModelObj_Temp.updateMatrixWorld();

        const outerRoofMesh_CSG = CSG.fromMesh(outerRoofMeshPosUpdate);
        const curveOuterMesh_CSG = CSG.fromMesh(curveOuterMeshPosUpdate)
        const curveInnerMesh_CSG = CSG.fromMesh(innerOuterMeshPosUpdate)

        const cutMesh_CSG1 = CSG.fromMesh(roofCutMesh1);
        const cutMesh_CSG2 = CSG.fromMesh(roofCutMesh2);
        const curveRoofCut_CSG1 = CSG.fromMesh(curveRoofCutMesh1)
        const curveRoofCut_CSG2 = CSG.fromMesh(curveRoofCutMesh2)

        let roofMeshFinalCsg
        let roofMeshFinalCsg_temp

        if(params.add_right_back_lean_porch && params.add_right_front_lean_porch) {
            roofMeshFinalCsg_temp = outerRoofMesh_CSG.subtract(cutMesh_CSG1);
            roofMeshFinalCsg = roofMeshFinalCsg_temp.subtract(cutMesh_CSG2)
        } else if (!params.add_right_back_lean_porch && params.add_right_front_lean_porch) roofMeshFinalCsg = outerRoofMesh_CSG.subtract(cutMesh_CSG1);
        else if (params.add_right_back_lean_porch && !params.add_right_front_lean_porch)roofMeshFinalCsg = outerRoofMesh_CSG.subtract(cutMesh_CSG2);
        else {
            roofMeshFinalCsg = outerRoofMesh_CSG 
        }
        // let roofMeshFinalCSG
        // if(params.p_r_s === 1 || !isEqualHeight || (params.singleSlope && params.freeStandingSide === 'left') || (controls.isCheckActiveTab === 'doorwindow' && const_var.focusedSection === 'rightlean')) {
        //     roofMeshFinalCSG = roofMeshFinalCsg;
        // } else {
        //     roofMeshFinalCSG = roofMeshFinalCsg.subtract(roofDelModelObj_CSG);
        // }

        const curveOuterMesh_CSG_temp_1 = curveOuterMesh_CSG.subtract(curveRoofCut_CSG1);
        const curveOuterMesh_CSG_temp_2 = curveOuterMesh_CSG_temp_1.subtract(curveRoofCut_CSG2);
        const curveInnerMesh_CSG_temp_1 = params.add_right_front_lean_porch ? curveInnerMesh_CSG.subtract(curveRoofCut_CSG1) : curveInnerMesh_CSG;
        const curveInnerMesh_CSG_temp_2 = params.add_right_back_lean_porch ? curveInnerMesh_CSG_temp_1.subtract(curveRoofCut_CSG2) : curveInnerMesh_CSG_temp_1;
    
        const roofMeshFinal = CSG.toMesh(roofMeshFinalCsg,new THREE.Matrix4())
        const curveOuterMeshFinal = CSG.toMesh(curveOuterMesh_CSG_temp_2, new THREE.Matrix4())
        const curveInnerMeshFinal = CSG.toMesh(curveInnerMesh_CSG_temp_2, new THREE.Matrix4())

        mainOuter = roofMeshFinal.geometry;
        curveOuter = curveOuterMeshFinal.geometry;
        curveInner = curveInnerMeshFinal.geometry;

        return {mainOuter, mainInner, curveOuter, curveInner}
    }, [params.b_l_t_r_pR, params.leanR_p_h, params.leanR_p_w, roofCutShapeWidth, params.add_right_back_lean_porch, params.add_right_front_lean_porch, isEqualHeight, roofAngle, params.p_r_s, params.p_w, params.p_d, params.leanR_p_pos, patternLength, patternDepth, curveRoofPatternDepth, curveRoofPositionZ, patternPosition, params.o_v_h_n_g, params.o_v_h_n_g_e, params.p_r_p, overHangWidth, isEqualHeight, params.singleSlope, params.freeStandingSide, sideOverHangSize, endOverHangSize, controls.isCheckActiveTab, const_var.focusedSection, isPattern])

    const roofPanel = useMemo(() => {

        let frontAlignmentVal = (params.leantoAlignmentRight == 3 || params.leantoAlignmentRight == 1) ? overHangLength : 0
        let backAlignmentVal = (params.leantoAlignmentRight == 2 || params.leantoAlignmentRight == 1) ? overHangLength : 0

        const model = new THREE.Shape();
        model.moveTo(-(params.leanR_p_d / 2 ) - frontAlignmentVal, params.p_h - (params.leanR_p_h + (params.leanR_p_w*(params.b_l_t_r_pR/12)) + 0.02));
        model.lineTo(params.leanR_p_d / 2 + backAlignmentVal, params.p_h - (params.leanR_p_h + (params.leanR_p_w*(params.b_l_t_r_pR/12)) + 0.02));
        model.lineTo(params.leanR_p_d / 2 + backAlignmentVal, - 0.2);
        model.lineTo(-params.leanR_p_d / 2 - frontAlignmentVal, - 0.2);
        model.closePath();

        return model;
    }, [params.leanR_p_d, params.leanR_p_h, params.p_d, params.p_h, params.leanR_p_w,params.b_l_t_r_pR, params.leantoAlignmentRight, overHangLength])

    useMemo(() => {
        if (params.p_r_s === 1 || params.p_r_s === 2) {
            if(params.p_r_s === 2) {
                if (params.o_v_h_n_g === true){
                    setOverHangWidth(sideOverHangSize)
                } else {
                    setOverHangWidth(0.4);
                }
                if (params.o_v_h_n_g_e === true) {
                    //setOverHangLength(endOverHangSize)
                    setOverHangLength(endOverHangSize == 0.5 ? 0.75 : endOverHangSize )
                } else {
                    setOverHangLength(0.75)
                }
            } else {
                setOverHangWidth(0)
                setOverHangLength(0.75)
            };
            if(params.p_r_s === 2) setPatternLength(diagonalLength ); else setPatternLength(diagonalLength + 0.02 - const_var.roofRegularDst / Math.cos(Math.atan(params.b_l_t_r_pR / 12)));
            if( params.add_right_back_lean_porch === true && params.add_right_front_lean_porch !== true ) {
                setPatternDepth((params.leanR_p_d) + overHangWidth + overHangLength * 2 + cbFrontBackWallOffset + params.leanR_p_w + patternLengthOffset )
                setPatternPosition({x:(params.leanR_p_d/2) + overHangWidth + overHangLength + cbFrontBackWallOffset + params.leanR_p_w + patternPositionOffset, y:-(diagonalLength-(overHangWidth)-(roofThickness * 2)), z:0.005})
                setCurveRoofPatternDepth((params.leanR_p_d) + overHangWidth + overHangLength + cbFrontBackWallOffset + params.leanB_p_w + 0.945 + patternLengthOffset)
                setCurveRoofPositionZ(params.leanR_p_d / 2 + 0.55 + ((params.leanR_p_d === params.p_d && isEqualFrontLeanHeight) ? -(overHangLength - 0.23) : 0))
            } else if( params.add_right_back_lean_porch !== true && params.add_right_front_lean_porch === true ) {
                setPatternDepth((params.leanR_p_d) + overHangWidth + overHangLength * 2 + cbFrontBackWallOffset + params.leanR_p_w + patternLengthOffset / 2)
                setPatternPosition({x:(params.leanR_p_d/2) + overHangLength + patternPositionOffset, y:-(diagonalLength-(overHangWidth)-(roofThickness * 2)), z:0.005})
                setCurveRoofPatternDepth((params.leanR_p_d) + overHangWidth + overHangLength + cbFrontBackWallOffset + params.leanF_p_w + 1 + patternLengthOffset)
                setCurveRoofPositionZ(params.leanR_p_d / 2 + params.leanF_p_w + 0.83)
            } else if( params.add_right_back_lean_porch === true && params.add_right_front_lean_porch === true ) {
                setPatternDepth((params.leanR_p_d) + (overHangWidth*2) + (cbFrontBackWallOffset*2) + (params.leanR_p_w*2) + (params.p_r_s === 1 ? 1 : 0))
                setPatternPosition({x:(params.leanR_p_d/2) + overHangWidth + cbFrontBackWallOffset + params.leanR_p_w, y:-(diagonalLength-(overHangWidth)-(roofThickness * 2)), z:0.005})
                setCurveRoofPatternDepth((params.leanR_p_d) + (overHangWidth*2) + (cbFrontBackWallOffset*2) + params.leanF_p_w + params.leanB_p_w + 1)
                setCurveRoofPositionZ((params.p_d / 2 + params.leanR_p_w + 0.5))
            } else {
                setPatternDepth(params.leanR_p_d + ((overHangLength)*2) + patternLengthOffset)
                setPatternPosition({x:(params.leanR_p_d + ((overHangLength)*2) )/2 + patternPositionOffset, y:-(diagonalLength-(overHangWidth)-(roofThickness * 2) + (params.p_r_s === 1 && -0.1)) + (params.p_r_s === 1 && -0.1), z:0})
                setCurveRoofPatternDepth(params.leanR_p_d + ((overHangLength)*2) + patternLengthOffset - 0.38)
                setCurveRoofPositionZ((params.leanR_p_d / 2 + regularPatternPositionOffset + 0.56))
            }
        } else if (params.p_r_s === 3) {
            if (params.o_v_h_n_g === true){
                setOverHangWidth(sideOverHangSize)
            } else {
                setOverHangWidth(0.4);
            }
            if (params.o_v_h_n_g_e === true) {
                //setOverHangLength(endOverHangSize)
                setOverHangLength(endOverHangSize == 0.5 ? 0.75 : endOverHangSize )
            } else {
                setOverHangLength(0.75)
            }
            if( params.add_right_back_lean_porch === true && params.add_right_front_lean_porch !== true ) {
                setPatternLength((params.leanR_p_d) + overHangWidth + overHangLength * 2 + cbFrontBackWallOffset + params.leanR_p_w + patternLengthOffset / 2)
                setPatternPosition({x:-(params.leanR_p_d/2) - overHangLength + patternPositionOffset, y:-diagonalLength + overHangWidth, z:0.005})
            } else if( params.add_right_back_lean_porch !== true && params.add_right_front_lean_porch === true ) {
                setPatternLength((params.leanR_p_d) + overHangWidth + overHangLength * 2 + cbFrontBackWallOffset + params.leanR_p_w + patternLengthOffset)
                setPatternPosition({x:-(params.leanR_p_d/2) - params.leanR_p_w - overHangWidth - overHangLength - cbFrontBackWallOffset + patternPositionOffset, y:-diagonalLength+ overHangWidth, z:0.005})
            } else if( params.add_right_back_lean_porch === true && params.add_right_front_lean_porch === true ) {
                setPatternLength((params.leanR_p_d) + (overHangWidth*2) + (cbFrontBackWallOffset*2) + (params.leanR_p_w*2))
                setPatternPosition({x:-(params.leanR_p_d/2) - overHangWidth - cbFrontBackWallOffset - params.leanR_p_w, y:-diagonalLength+ overHangWidth, z:0.005})
            } else {
                setPatternLength(params.leanR_p_d + ((overHangLength)*2) + patternLengthOffset)
                setPatternPosition({x:(-params.leanR_p_d - ((overHangLength)*2) )/2 + patternPositionOffset, y:-diagonalLength+ overHangWidth, z:0.005})
            }
            // setPatternLength(params.leanR_p_d + ((overHangLength+ cbFrontBackWallOffset)*2))
            setPatternDepth(diagonalLength)
            // setPatternPosition({x:(params.leanR_p_d + ((overHangLength+ cbFrontBackWallOffset)*2) )/2,y:-diagonalLength + (overHangWidth)+(roofThickness * 2),z:0.005})
        }
    }, [params.p_r_s, params.p_d, params.o_v_h_n_g_e, params.o_v_h_n_g, overHangWidth,params.leanR_p_d,params.leanR_p_w,params.add_right_back_lean_porch,params.add_right_front_lean_porch, diagonalLength, overHangLength, cbFrontBackWallOffset, patternLengthOffset,patternPositionOffset, sideOverHangSize, endOverHangSize, params.leanF_p_w, params.leanB_p_w, regularPatternPositionOffset])

    useMemo(() => {
        let panelHeight = (params.leanR_p_h + (params.leanR_p_w*(params.b_l_t_r_pR/12))) - 0.2
        if(params.p_d != params.leanR_p_d && rightWallCloseState == panelHeight){
            setIsRoofPanel(false)
        }else{
            setIsRoofPanel(false)
        }
    }, [params.leanR_p_w, params.leanR_p_h, params.leanR_p_d, params.p_d, params.p_h, params.b_l_t_r_pR,rightWallCloseState])

    const handleMouseEvent = (e) => {
        if (controls.isCheckActiveTab === 'doorwindow' && (const_var.focusedSection === "rightlean" && params.activeWall === "R_L_F_S_W")) {
            switch (e.type) {
                case 'pointerdown':
                case 'click':
                case 'pointermove':
                    e.stopPropagation();
                    break;
                default :
                    break
            }
        }
    };
    const eventHandlers = useMouseEvents(handleMouseEvent);

    const texture = useMemo(() => {
        let texture = (const_var.wallTextureObj["vertical"] != undefined) ? const_var.wallTextureObj["vertical"].clone() : const_var.wallTextureObj["vertical"];
        textureAnisotropy(gl, texture, 1.3, 1.3, Math.PI/-2);    
        return texture
    }, [params.p_v_w,params.wallTexture,const_var.wallTextureObj[params.wallTexture]])

    return (
        <group {...eventHandlers}>    
            <group position={[(params.p_w / 2 + params.leanR_p_w + wallThickness * 2), params.leanR_p_h + 0.025 - wallThickness, 0]} rotation={[0, Math.PI, (Math.PI / 2 + roofAngle)]} >
                {params.p_r_s !== 1 && <group rotation={[0, -Math.PI / 2, 0]}>
                    <mesh name='roof-in' position={[0, 0, roofThickness * 2]}>
                        <extrudeGeometry args={[roofPanModel, extrudeSettings(roofThickness)]} />
                        <MaterialInnerRoof texture={roofTexture} visibleSection={'rightLean'} />
                    </mesh>

                    {/* {isRoofPanel && <group name='roof-panel-right' rotation={[Math.PI / -2 - roofAngle, 0, 0]} position = {[0,-Math.sqrt(Math.pow(params.leanR_p_w, 2) + Math.pow((params.b_l_t_r_pR / 12*(params.leanR_p_w)), 2)),0]}>
                        <mesh>
                            <extrudeGeometry args={[roofPanel, extrudeSettings(roofThickness)]} />
                            <MaterialOuterRoof isPattern={isPattern} texture={texture} visibleSection={"rightLean"} />
                        </mesh>
                        <mesh position={[0,0,-0.005]}>
                            <extrudeGeometry args={[roofPanel, extrudeSettings(roofThickness)]} />
                            <MaterialInnerRoof texture={roofTexture} visibleSection={'rightLean'} />
                        </mesh>
                    </group>} */}
                </group>}
            </group>
            <group>
                <mesh name="roof-out" geometry={roofModelObj?.mainOuter} position={[0, 0.043, 0]} renderOrder={1}>
                    <MaterialOuterRoof isPattern={isPattern} texture={texture} visibleSection={"rightLean"} />
                </mesh>
                {/* <mesh name="roof-out" geometry={curveRoofMeshFinal2.geometry} >
                    <meshStandardMaterial  color={darkenColor(roofColor,15)} side={THREE.DoubleSide} roughness={0.85} metalness={0.9}/>
                </mesh> */}
            </group>
            <group >
                { (params.p_r_s === 3 && params.add_right_back_lean_porch) &&
                    <mesh name='roof-top-right-trim-ridge' position={[0, 0.03, 0]} geometry={roofTopTrimRidgeModelObj?.rightTrimRidge}  >
                        <MaterialRoofRidge isPattern={isPattern} visibleSection={'rightLean'} />
                    </mesh>
                }
                { (params.p_r_s === 3 && params.add_right_front_lean_porch) &&
                    <mesh name='roof-top-left-trim-ridge' position={[0, 0.03, 0]} geometry={roofTopTrimRidgeModelObj?.leftTrimRidge}  >
                        <MaterialRoofRidge isPattern={isPattern} visibleSection={'rightLean'} />
                    </mesh>
                }
            </group>
            
            {params.p_r_s === 1 && roofModelObj?.curveOuter && roofModelObj?.curveInner &&
                <>
                    <mesh name="roof-out-right-curve" position={[0, 0.03, 0]} geometry={roofModelObj?.curveOuter} renderOrder={1}>
                        <MaterialOuterRoof isPattern={isPattern} texture={texture} visibleSection={"rightLean"} />
                    </mesh>
                    <mesh name="roof-inner-front-curve" geometry={roofModelObj?.curveInner} >
                        <MaterialInnerRoof texture={horizontalMap} visibleSection={'rightLean'} />
                    </mesh>
                </>
            }
        </group>
    )
}

export default RoofModel